import {
  EmailRounded,
  KeyboardArrowDownRounded,
  PhoneRounded,
  WhatsApp,
} from "@mui/icons-material";
import { Menu, MenuItem } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchQuiz, fetchTemasDisponiveis } from "../game/api";
import "../game/game.css";
import {
  buildQuestions,
  clamp,
  maxPerTeam,
  DEFAULT_TEAM_NAMES,
  MAX_TEAMS,
  MIN_TEAMS,
  QUESTION_LIMITS,
  TEAM_COLORS,
  TemaOption,
  TIME_LIMITS,
} from "../game/logic";
import Logo from "../components/logo";
import FontControl from "../game/font-control";
import Stepper from "../game/stepper";
import useGameStore from "../game/store";
import { useUserStore } from "../store/user-store";

const TEMA_COLORS = [...TEAM_COLORS, "#2F9E6E"];

export default function GameConfigPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useUserStore();
  const { config, setConfig, setTeamCount, startSession } = useGameStore();
  const { teamNames, seconds, questionsPerTeam, temaId } = config;
  const teamCount = teamNames.length;

  const [temas, setTemas] = useState<TemaOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);

  const loadTemas = () => {
    setLoading(true);
    setLoadError(false);
    fetchTemasDisponiveis()
      .then((list) => {
        const usable = list.filter((t) => t.total > 0);
        setTemas(usable);
        // tema guardado que já não existe
        if (temaId && !usable.some((t) => t.id === temaId)) {
          setConfig({ temaId: undefined, temaNome: undefined });
        }
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  };

  useEffect(loadTemas, []); // eslint-disable-line react-hooks/exhaustive-deps

  const tema = useMemo(() => temas.find((t) => t.id === temaId), [temas, temaId]);
  // O número de perguntas é por equipe: o total da partida é perguntas × equipes.
  const capPerTeam = tema
    ? Math.min(QUESTION_LIMITS.max, maxPerTeam(tema.levels, teamCount))
    : QUESTION_LIMITS.max;
  const notEnough = !!tema && capPerTeam < QUESTION_LIMITS.min;
  const maxQuestionsPerTeam = Math.max(QUESTION_LIMITS.min, capPerTeam);
  const perTeam = clamp(questionsPerTeam, QUESTION_LIMITS.min, maxQuestionsPerTeam);
  const totalToPlay = perTeam * teamCount;

  const selectTema = (t: TemaOption) => {
    setConfig({
      temaId: t.id,
      temaNome: t.nome,
      questionsPerTeam: clamp(
        questionsPerTeam,
        QUESTION_LIMITS.min,
        Math.max(QUESTION_LIMITS.min, Math.min(QUESTION_LIMITS.max, maxPerTeam(t.levels, teamCount))),
      ),
    });
    setAnchor(null);
  };

  const setTeamName = (index: number, name: string) =>
    setConfig({ teamNames: teamNames.map((n, i) => (i === index ? name : n)) });

  const start = async () => {
    if (!tema || starting) return;
    setStarting(true);
    setStartError(null);
    try {
      const quiz = await fetchQuiz(tema.id);
      const questions = buildQuestions(quiz, perTeam, teamCount);
      if (questions.length === 0) throw new Error("sem perguntas");
      startSession(questions, quiz);
      navigate("/jogo");
    } catch {
      setStartError("Não foi possível carregar as perguntas. Verifique a ligação e tente de novo.");
      setStarting(false);
    }
  };

  const selectedColor = tema
    ? TEMA_COLORS[temas.indexOf(tema) % TEMA_COLORS.length]
    : "#C7C4D1";

  return (
    <div className="qz-root">
      <header className="qz-head">
        <div className="qz-brand">
          <Logo size={48} />
          <span className="qz-brand-name qz-serif">Aprende+</span>
        </div>
        <div className="qz-head-center">
          <h1 className="qz-title qz-serif">Configurar partida</h1>
          <p className="qz-subtitle">Ajuste o jogo antes de começar</p>
        </div>
        <button
          type="button"
          className="qz-btn qz-btn-outline qz-btn-sm"
          onClick={() => navigate(isAuthenticated() ? "/painel" : "/login")}
        >
          {isAuthenticated() ? "Painel" : "Entrar"}
        </button>
      </header>

      <div className="qz-config-grid">
        <section className="qz-card qz-config-main" aria-label="Equipes e tema">
          <div className="qz-label" id="tema-label">Tema do quiz</div>
          <button
            type="button"
            className="qz-select"
            aria-haspopup="listbox"
            aria-expanded={Boolean(anchor)}
            aria-labelledby="tema-label tema-value"
            disabled={loading || loadError}
            onClick={(e) => setAnchor(e.currentTarget)}
          >
            <span className="qz-dot" style={{ background: selectedColor }} />
            <span
              id="tema-value"
              className={`qz-select-text ${tema ? "" : "is-placeholder"}`}
            >
              {loading
                ? "A carregar temas…"
                : loadError
                  ? "Não foi possível carregar os temas"
                  : (tema?.nome ?? "Escolha um tema")}
            </span>
            <KeyboardArrowDownRounded style={{ color: "#6B6685" }} />
          </button>
          {loadError && (
            <p className="qz-error" role="alert">
              Sem ligação ao servidor.{" "}
              <button type="button" className="qz-link" onClick={loadTemas}>
                Tentar novamente
              </button>
            </p>
          )}
          <Menu
            anchorEl={anchor}
            open={Boolean(anchor)}
            onClose={() => setAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            slotProps={{
              paper: {
                sx: {
                  minWidth: anchor?.offsetWidth,
                  maxHeight: 320,
                  mt: 0.5,
                  borderRadius: "12px",
                  border: "1px solid #E2E4E0",
                  boxShadow: "0 16px 40px -16px rgba(27,24,48,0.25)",
                },
              },
            }}
            MenuListProps={{ role: "listbox" }}
          >
            {temas.map((t, i) => (
              <MenuItem
                key={t.id}
                role="option"
                selected={t.id === temaId}
                onClick={() => selectTema(t)}
                sx={{ gap: 1.5, fontFamily: "inherit", fontSize: 14 }}
              >
                <span
                  className="qz-dot qz-dot-sm"
                  style={{ background: TEMA_COLORS[i % TEMA_COLORS.length] }}
                />
                <span style={{ flex: 1 }}>{t.nome}</span>
                <span className="qz-hint">{t.total} perguntas</span>
              </MenuItem>
            ))}
          </Menu>

          <hr className="qz-hr" />

          <div className="qz-label">Número de equipes</div>
          <div className="qz-row">
            <Stepper
              label="Número de equipes"
              value={teamNames.length}
              min={MIN_TEAMS}
              max={MAX_TEAMS}
              onChange={setTeamCount}
            />
            <span className="qz-hint">de {MIN_TEAMS} a {MAX_TEAMS} equipes</span>
          </div>

          <div className="qz-label" style={{ marginTop: 22 }}>Nomes das equipes</div>
          {teamNames.map((name, i) => (
            <div className="qz-team-row" key={i}>
              <span className="qz-dot qz-dot-sm" style={{ background: TEAM_COLORS[i] }} />
              <input
                className="qz-input"
                value={name}
                maxLength={24}
                placeholder={DEFAULT_TEAM_NAMES[i]}
                aria-label={`Nome da equipe ${i + 1}`}
                onChange={(e) => setTeamName(i, e.target.value)}
              />
            </div>
          ))}
          <div className="qz-row" style={{ marginTop: 16 }}>
            <button
              type="button"
              className="qz-link"
              disabled={teamNames.length >= MAX_TEAMS}
              onClick={() => setTeamCount(teamNames.length + 1)}
            >
              + Adicionar equipe
            </button>
            <span className="qz-hint">({teamNames.length} de {MAX_TEAMS} usadas)</span>
          </div>

          <hr className="qz-hr" />
          <div className="qz-label">Cores são atribuídas automaticamente</div>
          <p className="qz-hint" style={{ margin: "8px 0 0", fontSize: 12 }}>
            Cada equipe recebe uma cor para facilitar o acompanhamento no placar.
          </p>

          <hr className="qz-hr" />
          <div className="qz-dev">
            <div className="qz-dev-role">Desenvolvedor</div>
            <div className="qz-dev-name">Francisco Rafael Tamele</div>
            <a className="qz-dev-link qz-dev-mail" href="mailto:franciscotamelle@gmail.com">
              <EmailRounded fontSize="inherit" />
              franciscotamelle@gmail.com
            </a>
            <a
              className="qz-dev-link qz-dev-wa"
              href="https://wa.me/258845857656"
              target="_blank"
              rel="noopener noreferrer"
            >
              <WhatsApp fontSize="inherit" />
              +258 845 857 656 (WhatsApp)
            </a>
            <a className="qz-dev-link qz-dev-tel" href="tel:+258865857656">
              <PhoneRounded fontSize="inherit" />
              +258 865 857 656
            </a>
          </div>
        </section>

        <aside className="qz-card qz-config-side" aria-label="Regras da partida">
          <div className="qz-label">Tempo por questão</div>
          <Stepper
            wide
            label="Tempo por questão"
            unit="s"
            value={seconds}
            min={TIME_LIMITS.min}
            max={TIME_LIMITS.max}
            step={TIME_LIMITS.step}
            onChange={(v) => setConfig({ seconds: v })}
          />

          <hr className="qz-hr" />

          <div className="qz-label">Perguntas por equipe</div>
          <Stepper
            wide
            label="Perguntas por equipe"
            value={perTeam}
            min={QUESTION_LIMITS.min}
            max={maxQuestionsPerTeam}
            onChange={(v) => setConfig({ questionsPerTeam: v })}
          />
          {tema && (
            <p className="qz-hint" style={{ margin: "8px 0 0" }}>
              {notEnough
                ? `Este tema não tem perguntas suficientes para ${teamCount} equipes (mínimo ${QUESTION_LIMITS.min} por equipe).`
                : `${totalToPlay} no total (${perTeam} × ${teamCount} equipes)`}
            </p>
          )}

          <hr className="qz-hr" />

          <div className="qz-label">Tamanho da fonte</div>
          <FontControl
            label="Pergunta"
            value={config.fontQuestion}
            onChange={(v) => setConfig({ fontQuestion: v })}
          />
          <FontControl
            label="Alternativas"
            value={config.fontOption}
            onChange={(v) => setConfig({ fontOption: v })}
          />

          <hr className="qz-hr" />

          <div className="qz-summary" aria-label="Resumo">
            <span className="qz-label">Resumo</span>
            <span>{teamNames.length} equipes</span>
            <span>{perTeam} perguntas por equipe</span>
            <span>{totalToPlay} no total · {seconds}s cada</span>
          </div>

          <div style={{ marginTop: "auto", paddingTop: 20 }}>
            {startError && (
              <p className="qz-error" role="alert">{startError}</p>
            )}
            <button
              type="button"
              className="qz-btn qz-btn-primary"
              style={{ width: "100%", marginTop: startError ? 10 : 0 }}
              disabled={!tema || starting || notEnough}
              onClick={start}
            >
              {starting ? "A preparar…" : tema ? "Iniciar jogo" : "Escolha um tema"}
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
}
